import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSearchParams } from "react-router-dom";

import { compensationsListLimit, compensationsListLimits } from "configs/limits";
import { useAppSelector, useAppDispatch } from "store/index";
import {
  getPlayerCompensationsList,
  handleCompensationsPaginate,
} from "store/players/list/slice";

import Pagination from "components/Pagination";
import RewardBonusCell from "components/rewardBonus/RewardBonusCell";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import EmptyState from "components/EmptyState/EmptyState";
import Tooltip from "components/Tooltip";
import { resolveRewardBonusDisplay } from "helpers/rewardBonusDisplay";
import { formatTime, useTimeFormat } from "helpers/timeFormat";
import {
  COMPENSATION_TYPE_LABELS,
  getCellText,
  truncateComment,
} from "./financeTabUtils";

const ListCompensationsTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const timeFormat = useTimeFormat();

  const compensationsList = useAppSelector(
    (store) => store.players.compensationsList,
  );
  const totalItems = useAppSelector(
    (store) => store.players.totalItemsCompensations,
  );
  const totalPages = useAppSelector(
    (store) => store.players.totalPagesCompensations,
  );
  const activePage = useAppSelector(
    (store) => store.players.activePageCompensations,
  );
  const isFetched = useAppSelector(
    (store) => store.players.isCompensationsFetched,
  );

  const [limit, setLimit] = useState(compensationsListLimit);
  const userId = searchParams.get("entityId");

  useEffect(() => {
    if (!userId) return;

    dispatch(
      getPlayerCompensationsList({
        userId: Number(userId),
        limit,
        offset: activePage - 1,
      }),
    );
  }, [activePage, dispatch, limit, userId]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handleCompensationsPaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handleCompensationsPaginate(activePage + 1));
    }
  };

  const renderTooltipCell = ({
    display,
    title,
  }: {
    display: string;
    title?: string;
  }) => {
    if (!title) {
      return <span className="finance-cell-text">{display}</span>;
    }

    return (
      <Tooltip text={title} delay={120}>
        <span className="finance-cell-text">{display}</span>
      </Tooltip>
    );
  };

  return (
    <div className="player-content-wrapper">
      {!isFetched ? (
        <ModalTabSkeleton />
      ) : compensationsList.length === 0 ? (
        <EmptyState message={t("noPlayerCompensationsFound")} />
      ) : (
        <>
          <div className="inner-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t("type")}</th>
                  <th>{t("amount")}</th>
                  <th>{t("wager")}</th>
                  <th>Transaction Code</th>
                  <th>Promo ID / Bonus ID</th>
                  <th>Internal Comment</th>
                  <th>{t("admin")}</th>
                  <th>{t("status")}</th>
                  <th>{t("created")}</th>
                </tr>
              </thead>

              <tbody>
                {compensationsList.map((row) => {
                  const comment = truncateComment(row.internalComment);
                  const type = getCellText(
                    COMPENSATION_TYPE_LABELS[row.compensationType] ??
                      row.compensationType ??
                      "—",
                  );
                  const transactionCode = getCellText(row.transactionCode);
                  const promoOrBonusId = getCellText(
                    row.promoOrBonusId ?? row.bonusId,
                  );
                  const bonusDisplay = resolveRewardBonusDisplay(row);
                  const admin = getCellText(row.operatorName);
                  const status = getCellText(row.statusCode);

                  return (
                    <tr key={row.operationId}>
                      <td>{row.operationId}</td>
                      <td>{renderTooltipCell(type)}</td>
                      <td>{`${row.amount} ${row.currency}`}</td>
                      <td>{row.wagerMultiplier}</td>
                      <td>{renderTooltipCell(transactionCode)}</td>
                      <td>
                        {bonusDisplay.mode === "plain" ? (
                          renderTooltipCell(promoOrBonusId)
                        ) : (
                          <RewardBonusCell
                            compensationType={row.compensationType}
                            bonusId={row.bonusId}
                            bonusTitle={row.bonusTitle}
                            bonusImage={row.bonusImage}
                          />
                        )}
                      </td>
                      <td>
                        {comment.title ? (
                          <Tooltip text={comment.title} delay={120}>
                            <span className="finance-comment-cell">
                              {comment.display}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="finance-comment-cell">
                            {comment.display}
                          </span>
                        )}
                      </td>
                      <td>{renderTooltipCell(admin)}</td>
                      <td>{renderTooltipCell(status)}</td>
                      <td className="date">
                        <span>
                          {moment(row.createdAt).format("DD.MM.YYYY")}
                        </span>
                        <span>{formatTime(row.createdAt, timeFormat)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {compensationsList.length > 0 && (
            <Pagination
              limit={limit}
              activePage={activePage}
              totalItems={totalItems}
              totalPages={totalPages}
              limitsArray={compensationsListLimits}
              handleLimitClick={(nextLimit) => {
                setLimit(nextLimit);
                dispatch(handleCompensationsPaginate(1));
              }}
              handleNextClick={handleNextClick}
              handlePrevClick={handlePrevClick}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ListCompensationsTab;
